import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import moment from 'moment';
import {
  toLocalDateFromInstant,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';
import { english } from 'src/i18n/language';

// The referral form's triage select still reads this fixed list. The five
// option lists kept beside it for the dead *_Notification.jsx forms went with
// those forms (TGCLOUD-1390); every other live picker reads its values from the
// configured list (TGCLOUD-1784). Do not wire a new picker to a fixed array.
export const REFERRAL_TRIAGE = [
  { label: 'Immediate', value: 'immediate' },
  { label: 'Urgent', value: 'urgent' },
  { label: 'Semi-urgent', value: 'semi_urgent' },
  { label: 'Standard', value: 'standard' }
];

export const VARIANT_INTERPRETATION_OPTIONS = [
  { label: 'Pathogenic', value: 'Pathogenic' },
  { label: 'Likely Pathogenic', value: 'Likely Pathogenic' },
  {
    label: 'Variant of Uncertain Significance',
    value: 'Variant of Uncertain Significance'
  },
  { label: 'Likely Benign', value: 'Likely Benign' },
  { label: 'Benign', value: 'Benign' }
];

/**
 * A variant's interpretation as shown: one of the five options above in the reader's language, or
 * any other value (an import's, a lab's own wording) exactly as it is stored.
 */
export const variantInterpretationText = (value, t = english) => {
  const option = VARIANT_INTERPRETATION_OPTIONS.find((o) => o.value === value);
  return option ? t(option.label) : value;
};

// Whether a variant was inherited or acquired.
//
// This is not a cosmetic detail: a somatic variant cannot be transmitted, so
// it is not evidence of a Mendelian cause. With no way to record it, the
// pedigree analyzer had to treat every pathogenic variant in an affected
// person as heritable, which is how a somatic JAK2 driver on a myelofibrosis
// patient was reported as autosomal dominant vertical transmission.
//
// "Not stated" is the default and is deliberately NOT the same as germline:
// the analyzer treats it as potentially heritable (matching how records
// created before this field existed already behave), so somatic has to be
// stated positively to change anything.
export const VARIANT_ORIGIN_OPTIONS = [
  { label: 'Not stated', value: 'unknown' },
  { label: 'Germline (inherited)', value: 'germline' },
  { label: 'Somatic (acquired)', value: 'somatic' }
];

// THE DROP-DOWN VALUES MOVED OUT (TGCLOUD-1784).
//
// These three helpers used to read and write one Mixed blob on the group
// preferences document, with the shipped values hard-coded just above them.
// Both now live in the lookup values collection: the values are rows a site
// can enable, disable, order and default, and what ships is declared once in
// `backend/config/lookupDefinitions.js` rather than duplicated here.
//
// They are re-exported under their old names, with their old signatures,
// because nineteen files import them. Each picker moves to `useLookupList`
// and `useLookupMutations` in its own commit rather than all at once.
//
// Two things behave differently behind the same names, both deliberate. The
// delete now DISABLES the value rather than removing it, so it keeps its
// place for when someone adds it back and every record holding it is
// untouched. And the server enforces the role rule for the first time: a
// genetics user calling the save gets a 403 instead of a silent write, which
// the interface has always implied but nothing enforced.
export {
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from 'src/util/lookups/lookupsApi';

const getReferralTags = async (theme, memberId, familyId, response) => {
  if (!response)
    response = await axios.get(APICONSTANTS.referral, {
      params: { familyId }
    });

  let referralOptions = response.data;
  referralOptions = referralOptions.filter((referral) => {
    return referral.familyMemberId == memberId || referral.showInFamily;
  });
  let referralTags = [];
  for (const referral of referralOptions) {
    referralTags.push({
      _id: referral._id,
      label:
        referral.referredBy +
        ' ' +
        moment(referral.createdDate).format(theme.dateFormat)
    });
  }
  return referralTags;
};

const getConsentTags = async (theme, memberId, familyId, response) => {
  if (!response)
    response = await axios.get(APICONSTANTS.consent, {
      params: { familyId }
    });
  let options = response.data;
  options = options.filter((option) => {
    return option.familyMemberId == memberId;
  });
  let Tags = [];
  for (const option of options) {
    Tags.push({
      _id: option._id,
      label:
        option.consentType +
        ' ' +
        moment(option.createdDate).format(theme.dateFormat)
    });
  }
  return Tags;
};

// "Test created 12/03/2026": each record that names this one, for the message that refuses its
// deletion. `t` is the caller's: the record type is translated, the date is the record's.
const getDependencies = async (familyId, _id, dateFormat, t = english) => {
  let dependences = [];
  await axios
    .get(`/clinical-notes/all`, {
      params: { familyId }
    })
    .then((res) => {
      for (const note of res.data) {
        if (
          note.associatedReferral?._id == _id ||
          note.associatedConsent?._id == _id
        ) {
          dependences.push(
            t('{{noteType}} created {{date}}', {
              noteType: t(note.noteType),
              date: moment(note.createdAt).format(dateFormat)
            })
          );
        }
      }
    });
  return dependences;
};

//Keeping these here just in case I need them, for now they need fixing which I have no reason to do
//const getAppointmentTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.appointment}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.clinicName + ' ' + option.appointmmentDate.split('T')[0]);
//  }
//  return Tags;
//};

//const getContactTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.contact}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.contactType + ' ' + option.contactDate.split('T')[0]);
//  }
//  return Tags;
//};
//
//
//const getSampleTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.sample}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.sampleType + ' ' + option.dateTaken.split('T')[0]);
//  }
//  return Tags;
//};
//
//const getTestTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.test}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.testName + ' ' + option.resultDueDate.split('T')[0]);
//  }
//  return Tags;
//};
//
//const getAddressTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.address}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.postCode + ' ' + option.town);
//  }
//  return Tags;
//};
//
//const getClinicalNoteTags = async (response) => {
//  if (!response)
//    response = await axios.get(`/api/${APICONSTANTS.clinicalNote}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.clinician + ' ' + option.date.split('T')[0]);
//  }
//  return Tags;
//};

const getAddressFlags = async ({ familyId, familyMemberId }) => {
  const res = await axios.get(
    APICONSTANTS.fetchAddressFlags
      .replace(':familyId', familyId)
      .replace(':familyMemberId', familyMemberId)
  );

  return res.data || {};
};

// The field a record is dated by. A Test is dated by its date requested, or
// failing that its date received; a Consent by its consent date, or failing
// that its request date. A record type with no date of its own is dated by when
// it was created.
const recordDateField = (record) => {
  switch (record.noteType) {
    case 'Appointment':
      return 'appointmentDate';
    case 'Referral':
      return 'referralDate';
    case 'Test':
      return record.dateRequested ? 'dateRequested' : 'dateResultReceived';
    case 'Sample':
      return 'dateTaken';
    case 'Clinical Note':
      return 'clinicalNoteDate';
    case 'Contact':
      return 'contactDate';
    case 'Consent':
      return record.consentDate ? 'consentDate' : 'consentRequestDate';
    case 'Treatment':
      return 'treatmentDate';
    case 'Tissue Pathology':
      return 'pathReportDate';
    case 'Process':
      return 'processStartDate';
    case 'Disease':
      return 'dateReported';
    case 'Phenotype':
    case 'Genotype':
      return 'dateRecorded';
    default:
      // Risk Score, Pregnancy and anything else.
      return 'createdAt';
  }
};

export const getRecordDate = (record) => record[recordDateField(record)];

/**
 * The record's date (getRecordDate) as a local calendar day, read by how it
 * was WRITTEN (CLAUDE.md, Dates) — for display, and for anything else that
 * needs the day rather than the instant.
 *
 *   `createdAt`, a server timestamp      -> a true instant -> toLocalDateFromInstant
 *   every clinician-picked record date   -> mixed          -> toLocalDateFromPickerOrImport
 *     (the form's picker writes a local-anchored instant, the spreadsheet
 *      importers `new Date('YYYY-MM-DD')`, i.e. UTC midnight)
 *
 * `moment(value)` read every one of them by its local parts, so an imported
 * date showed a day early at any negative offset.
 *
 * @param {object} record
 * @returns {Date|null} null when the record has no readable date
 */
export const readRecordDate = (record) => {
  const field = recordDateField(record);
  return field === 'createdAt'
    ? toLocalDateFromInstant(record.createdAt)
    : toLocalDateFromPickerOrImport(record[field]);
};

// A risk record's kind, as ClinicalNotes labels it (RISK_LABELS): a model's own
// name (GAIL, CanRisk, PRS) is shown as it is, and a word is translated.
const RISK_LABEL_WORDS = new Set(['Questionnaire']);
export const riskLabelText = (label, t = english) =>
  RISK_LABEL_WORDS.has(label) ? t(label) : label;

// The record's own words, or its type's when it has none. `t` translates only
// those fallbacks; a record's values are never sent to it. Search reads the
// English (no `t`).
export const getRecordTitle = (record, t = english) => {
  switch (record.noteType) {
    case 'Appointment':
      return record.clinicName || record.appointmentType || t('Appointment');
    case 'Referral':
      return (
        record.referralReason ||
        record.referralType ||
        (record.referredBy
          ? t('Referral by {{name}}', { name: record.referredBy })
          : t('Referral'))
      );
    case 'Test':
      return record.testName || record.testType || t('Test');
    case 'Sample':
      return record.sampleType || record.trackingId || t('Sample');
    case 'Clinical Note':
      return (
        (record.note || '').split('\n')[0].slice(0, 100) || t('Clinical Note')
      );
    case 'Contact':
      return record.reasonForContact || record.contactType || t('Contact');
    case 'Consent':
      return record.consentType || t('Consent');
    case 'Treatment':
      return record.treatment || t('Treatment');
    case 'Pregnancy':
      return t('Pregnancy');
    case 'Address':
      return (
        [record.addressLine1, record.town || record.city]
          .filter(Boolean)
          .join(', ') || t('Address')
      );
    case 'Tissue Pathology':
      return (
        [record.tissueSite, record.morphology].filter(Boolean).join(' — ') ||
        t('Tissue Pathology')
      );
    case 'Process':
      return record.processType || t('Process');
    case 'Disease':
      return record.name || t('Disease');
    case 'Phenotype':
      return record.name || t('Phenotype');
    case 'Genotype':
      return record.name || t('Genotype');
    case 'Risk Score':
      return riskLabelText(record.riskLabel, t) || t('Risk Score');
    default:
      return record.noteType ? t(record.noteType) : t('Record');
  }
};

export const getSearchableText = (record) =>
  [
    getRecordTitle(record),
    record.noteType,
    record.note,
    record.referralReason,
    record.referralNotes,
    record.appointmentNotes,
    record.testNotes,
    record.testResult,
    record.testName,
    record.consentNotes,
    record.contactNotes,
    record.comment,
    record.reasonForContact,
    record.sampleType,
    record.sampleStatus,
    record.treatment,
    record.referralType,
    record.referralTo,
    record.referredBy,
    record.clinicName,
    record.clinician,
    record.testType,
    record.addressLine1,
    record.addressLine2,
    record.town,
    record.city,
    record.postCode,
    record.country,
    record.laboratory,
    record.tissueSite,
    record.morphology,
    record.consentType,
    record.contactType,
    record.id,
    record.carrierStatus,
    record.riskLabel,
    record.gailRiskScore != null ? String(record.gailRiskScore) : null,
    record.prsResult?.trait,
    record.prsResult?.pgsId
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export {
  getAddressFlags,
  getConsentTags,
  getReferralTags,
  getDependencies
  //getAddressTags,
  //getClinicalNoteTags,
  //getTestTags,
  //getSampleTags,
  //getContactTags,
  //getAppointmentTags
};
